/**
 * Context-aware guidance panel for person profile pages.
 * User-collapsible at all viewport sizes; preference stored in localStorage.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  ChevronRight,
  ClipboardList,
  Link2,
  Mic,
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { getApiBase } from '../../utils/apiBase';
import {
  buildCompletionSuggestions,
  buildFamilyInsights,
  buildMemorialReadiness,
  buildMemoryPrompts,
  resolveInsightAction,
} from '../../utils/personArchiveInsight';
import { memorialManageHref } from '../../utils/memorial';
import { storybookHref, storybookStartHref } from '../../utils/storybook';
import { LEGACY_BOOK_COPY } from '../../utils/humanCopy';
import { HelpContextLink } from '../help';

const API = getApiBase();

const COLLAPSIBLE_SCROLL_CLASS =
  'max-h-[min(28rem,calc(100vh-10rem))] overflow-y-auto overscroll-contain pr-1 -mr-1';
const SIDEBAR_SCROLL_CLASS =
  'max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain pr-1 -mr-1';

function PanelCard({ title, description, children, className }) {
  return (
    <section
      className={cn(
        'rounded-xl border border-[hsl(38_14%_86%)]/50 bg-[hsl(42_28%_99%)]/55 px-4 py-3.5',
        className,
      )}
    >
      {title ? <h3 className="font-serif text-sm font-medium text-[hsl(24_12%_20%)]">{title}</h3> : null}
      {description ? <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground/80">{description}</p> : null}
      <div className={title || description ? 'mt-3 space-y-2' : 'space-y-2'}>{children}</div>
    </section>
  );
}

function InsightListItem({ label, onClick, testId, subtle = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'group flex w-full items-start justify-between gap-2 rounded-lg px-2 py-1.5 text-left transition hover:bg-white/80',
        subtle ? 'text-xs text-muted-foreground' : 'text-sm text-[hsl(32_10%_32%)]',
      )}
    >
      <span className="leading-snug">{label}</span>
      <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-35 group-hover:opacity-70" aria-hidden />
    </button>
  );
}

function GuidanceHeader({ subtitle = 'Completion tips, prompts and readiness' }) {
  return (
    <div className="min-w-0">
      <p className="font-serif text-sm text-[hsl(24_12%_20%)]">Profile guidance</p>
      <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground/75">{subtitle}</p>
      <p className="mt-1.5">
        <HelpContextLink section="person-profiles">How to complete a profile</HelpContextLink>
      </p>
    </div>
  );
}

export function PersonArchiveInsightPanel({
  person,
  activeTab = 'overview',
  storyCount = 0,
  voiceMemoriesCount = 0,
  documentCount = 0,
  possessionCount = 0,
  familyConnectionCount = 0,
  relationships = null,
  canEdit = false,
  isGenealogical = true,
  onNavigate,
  onExportPdf,
  onOpenMemorial,
  exportingPdf = false,
  placement = 'inline',
  collapsed = true,
  onExpand,
  onCollapse,
  className,
}) {
  const [memorial, setMemorial] = useState(null);
  const [storybookId, setStorybookId] = useState(null);

  const isSidebar = placement === 'sidebar';

  useEffect(() => {
    if (!person?.id || !isGenealogical) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/memorials-api/by-person/${person.id}`)
      .then(({ data }) => {
        if (!cancelled) setMemorial(data?.memorial || null);
      })
      .catch(() => {
        if (!cancelled) setMemorial(null);
      });
    return () => {
      cancelled = true;
    };
  }, [person?.id, person?.tree_id, isGenealogical]);

  useEffect(() => {
    if (!person?.id) return undefined;
    let cancelled = false;
    import('../../utils/storybook')
      .then(({ storybookApi }) => storybookApi.list({ personId: person.id, treeId: person.tree_id }))
      .then((books) => {
        if (!cancelled) setStorybookId(Array.isArray(books) && books[0]?.id ? books[0].id : null);
      })
      .catch(() => {
        if (!cancelled) setStorybookId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [person?.id, person?.tree_id]);

  const ctx = useMemo(
    () => ({
      person,
      storyCount,
      voiceMemoriesCount,
      possessionCount,
      familyConnectionCount,
      memorial,
      isGenealogical,
      canEdit,
    }),
    [person, storyCount, voiceMemoriesCount, possessionCount, familyConnectionCount, memorial, isGenealogical, canEdit],
  );

  const suggestions = useMemo(() => buildCompletionSuggestions(ctx), [ctx]);
  const prompts = useMemo(() => buildMemoryPrompts(person), [person]);
  const memorialReadiness = useMemo(() => buildMemorialReadiness({ person, memorial }), [person, memorial]);
  const familyInsights = useMemo(
    () => buildFamilyInsights({ person, relationships, isGenealogical }),
    [person, relationships, isGenealogical],
  );

  const handlers = useMemo(
    () => ({
      onNavigate,
      onExportPdf,
      onOpenMemorial: onOpenMemorial || (() => person && window.open(memorialManageHref(person), '_self')),
    }),
    [onNavigate, onExportPdf, onOpenMemorial, person],
  );

  const runAction = useCallback(
    (action) => resolveInsightAction(action, handlers),
    [handlers],
  );

  const guidanceSubtitle =
    activeTab === 'overview'
      ? 'Completion tips, prompts and readiness'
      : 'Checklist and next steps';

  const content = (
    <div className="space-y-3">
      {activeTab === 'overview' ? (
        <>
          {canEdit && suggestions.length > 0 ? (
            <PanelCard title="Complete this profile" description="A few real details make the archive feel personal.">
              <ul className="divide-y divide-[hsl(38_14%_86%)]/50">
                {suggestions.map((item) => (
                  <li key={item.id}>
                    <InsightListItem
                      label={item.label}
                      onClick={() => runAction(item.action)}
                      testId={`insight-complete-${item.id}`}
                    />
                  </li>
                ))}
              </ul>
            </PanelCard>
          ) : null}

          <PanelCard title="Memory prompts" description="Start with one gentle question.">
            <ul className="space-y-1">
              {prompts.slice(0, 3).map((prompt) => (
                <li key={prompt} className="rounded-lg bg-white/50 px-2.5 py-2 text-xs leading-relaxed text-[hsl(32_10%_36%)]">
                  {prompt}
                </li>
              ))}
            </ul>
            {canEdit ? (
              <button
                type="button"
                className="mt-2 text-xs text-[#8B7355] underline-offset-2 hover:underline"
                onClick={() => onNavigate?.('stories')}
                data-testid="insight-prompt-add-story"
              >
                Add a story from a prompt
              </button>
            ) : null}
          </PanelCard>

          {memorialReadiness.length > 0 ? (
            <PanelCard title="Memorial readiness" description="When you are ready to share remembrance publicly.">
              <ul className="space-y-1">
                {memorialReadiness.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => !item.done && runAction(item.action)}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm',
                        item.done ? 'text-muted-foreground/70' : 'text-[hsl(32_10%_32%)] hover:bg-white/80',
                      )}
                    >
                      <span className={item.done ? 'line-through decoration-[hsl(38_14%_70%)]' : undefined}>{item.label}</span>
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground/60">
                        {item.done ? 'Done' : 'Add'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </PanelCard>
          ) : null}

          {isGenealogical ? (
            <PanelCard
              title="Legacy output readiness"
              description="Books and memorial pages draw from what you preserve here."
              className="border-[hsl(38_14%_86%)]/45 bg-[hsl(42_28%_99%)]/50"
            >
              <InsightListItem
                label={storyCount > 0 || documentCount > 0 ? 'Preview Legacy Book section' : 'Add content to preview a book section'}
                onClick={() => runAction({ type: 'export' })}
                testId="insight-legacy-preview-btn"
                subtle
              />
              <InsightListItem
                label="Open memorial workspace"
                onClick={() => runAction({ type: 'memorial' })}
                testId="insight-memorial-workspace"
                subtle
              />
              <InsightListItem
                label={storybookId ? 'Open Storybook' : 'Start Storybook'}
                onClick={() => {
                  window.location.href = storybookId
                    ? storybookHref(storybookId)
                    : storybookStartHref({ personId: person?.id, treeId: person?.tree_id });
                }}
                testId="insight-storybook-link"
                subtle
              />
            </PanelCard>
          ) : null}
        </>
      ) : null}

      {activeTab === 'stories' ? (
        <>
          <PanelCard title="Capture their voice" description="Speak naturally — transcription and polish can come later.">
            <p className="text-xs leading-relaxed text-muted-foreground">
              A few minutes in their own words often means more than a perfect paragraph.
            </p>
            <Button type="button" size="sm" className="mt-2 w-full rounded-full" onClick={() => onNavigate?.('stories', { focus: 'voice' })}>
              <Mic className="mr-1.5 h-3.5 w-3.5" />
              Record voice memory
            </Button>
          </PanelCard>
          <PanelCard title="Suggested prompts">
            {prompts.slice(0, 2).map((prompt) => (
              <p key={prompt} className="rounded-lg bg-white/50 px-2.5 py-2 text-xs leading-relaxed text-[hsl(32_10%_36%)]">
                {prompt}
              </p>
            ))}
          </PanelCard>
          {isGenealogical ? (
            <PanelCard title="Legacy Book" description="Completed stories can flow into your export when you are ready.">
              <Button type="button" size="sm" variant="outline" className="w-full rounded-full" onClick={() => runAction({ type: 'export' })}>
                {LEGACY_BOOK_COPY.downloadKeepsake}
              </Button>
              <Button type="button" size="sm" variant="ghost" className="w-full rounded-full text-xs" asChild>
                <Link to={storybookId ? storybookHref(storybookId) : storybookStartHref({ personId: person?.id, treeId: person?.tree_id })}>Open Storybook</Link>
              </Button>
            </PanelCard>
          ) : null}
        </>
      ) : null}

      {activeTab === 'documents' ? (
        <>
          <PanelCard title="Photos & documents" description="Attach certificates, letters, and images to the right person.">
            <p className="text-xs leading-relaxed text-muted-foreground">
              Add a short note to each upload — future readers will thank you for the context.
            </p>
          </PanelCard>
          <PanelCard title="Priceless possessions">
            <InsightListItem
              label="Link a keepsake to its story"
              onClick={() => {
                onNavigate?.('overview');
                setTimeout(() => {
                  document.querySelector('[data-testid="priceless-possessions-section"], [data-testid="memory-markers-section"]')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 200);
              }}
            />
          </PanelCard>
        </>
      ) : null}

      {activeTab === 'family' ? (
        <>
          <PanelCard title="Family connections">
            {familyInsights.gaps.length > 0 ? (
              <ul className="space-y-1">
                {familyInsights.gaps.map((gap) => (
                  <li key={gap} className="text-xs leading-relaxed text-[hsl(32_10%_36%)]">{gap}</li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">Family links are in place for this profile.</p>
            )}
            {canEdit ? (
              <Button type="button" size="sm" variant="outline" className="mt-2 w-full rounded-full" onClick={() => onNavigate?.('family')}>
                <Link2 className="mr-1.5 h-3.5 w-3.5" />
                Add a connection
              </Button>
            ) : null}
          </PanelCard>
          {familyInsights.verificationNote ? (
            <PanelCard title="Verification">
              <p className="text-xs leading-relaxed text-muted-foreground">{familyInsights.verificationNote}</p>
            </PanelCard>
          ) : null}
        </>
      ) : null}

      {activeTab === 'shared' ? (
        <PanelCard title="Shared archive" description="Files here belong to the whole family — not only one person.">
          <InsightListItem label="Upload from Documents tab" onClick={() => onNavigate?.('documents')} />
        </PanelCard>
      ) : null}
    </div>
  );

  const scrollClass = isSidebar ? SIDEBAR_SCROLL_CLASS : COLLAPSIBLE_SCROLL_CLASS;
  const openLabel = isSidebar ? 'Open guidance' : 'Show checklist';

  if (collapsed) {
    return (
      <aside
        className={cn('w-full min-w-0', className)}
        data-testid="person-archive-insight-panel"
        data-insight-mode={isSidebar ? 'sidebar-collapsed' : 'inline-collapsed'}
      >
        <div className="rounded-xl border border-dashed border-[hsl(38_14%_86%)]/80 bg-[hsl(42_28%_99%)]/70 px-4 py-3.5 shadow-sm shadow-[hsl(38_14%_86%)]/10 transition hover:border-[hsl(38_18%_78%)]/70 hover:bg-[hsl(42_28%_99%)]/90">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[hsl(38_14%_86%)]/60 bg-white/70 text-muted-foreground/70">
              <ClipboardList className="h-4 w-4" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <GuidanceHeader subtitle={guidanceSubtitle} />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3 w-full rounded-full border-[hsl(38_14%_86%)]/70 text-xs font-normal sm:w-auto"
                onClick={onExpand}
                data-testid="person-insight-panel-open"
              >
                {openLabel}
              </Button>
            </div>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside
      className={cn('w-full min-w-0', className)}
      data-testid="person-archive-insight-panel"
      data-insight-mode={isSidebar ? 'sidebar-expanded' : 'inline-expanded'}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <GuidanceHeader subtitle={guidanceSubtitle} />
        <button
          type="button"
          onClick={onCollapse}
          className="shrink-0 text-xs text-muted-foreground underline-offset-2 hover:text-foreground/85 hover:underline"
          data-testid="person-insight-panel-hide"
        >
          Hide guidance
        </button>
      </div>
      <div className={cn('rounded-xl border border-[hsl(38_14%_86%)]/50 bg-[hsl(42_28%_99%)]/60 p-3', scrollClass)} data-testid="person-insight-panel-scroll">
        {content}
      </div>
    </aside>
  );
}

export default PersonArchiveInsightPanel;
